import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DashboardShell from "./DashboardShell";
import { toast } from "sonner";
import {
  Calendar, History, Heart, Bell, User, Loader2, Clock, Scissors, RefreshCw, Check, X,
} from "lucide-react";

const NAV = [
  { key: "upcoming", label: "Upcoming", icon: Calendar },
  { key: "history", label: "History", icon: History },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "profile", label: "Profile", icon: User },
];

export default function CustomerDashboard() {
  const { user, refreshUser, barbers, logout } = useAuth();
  const [active, setActive] = useState("upcoming");
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [reschedule, setReschedule] = useState(null);
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "", favourite_barber_id: user?.favourite_barber_id || "" });

  const load = () => {
    api.get("/appointments/mine").then((r) => setData(r.data)).catch(() => setData({ upcoming: [], history: [] }));
    api.get("/notifications").then((r) => setNotes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setProfile({ name: user?.name || "", phone: user?.phone || "", favourite_barber_id: user?.favourite_barber_id || "" }); }, [user]);

  const rebook = (a) => {
    const ids = (a.service_ids && a.service_ids.length ? a.service_ids : [a.service_id]).filter(Boolean);
    window.location.href = ids.length ? `/book?services=${ids.join(",")}` : "/book";
  };

  const cancelAppt = async (a) => {
    if (!window.confirm("Cancel this appointment? Online cancellation cannot be undone.")) return;
    try { await api.put(`/appointments/${a.id}/cancel`); toast.success("Appointment cancelled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    load();
  };

  const saveProfile = async () => {
    try {
      await api.put("/auth/profile", profile);
      await refreshUser();
      toast.success("Profile updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const favBarber = barbers.find((b) => b.id === user?.favourite_barber_id);

  return (
    <DashboardShell testid="customer-dashboard" title="Customer" nav={NAV} active={active} onNav={setActive} identity={user} onLogout={logout}>
      {data === null ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>
      ) : (
        <>
          {active === "upcoming" && (
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                <Stat label="Upcoming" value={data.upcoming.length} />
                <Stat label="Total Visits" value={data.history.length} />
                <Stat label="Favourite" value={favBarber ? favBarber.name.split(" ")[0] : "—"} />
                <Link to="/book" className="bg-[#D4AF37] text-black rounded-2xl p-5 flex flex-col justify-center hover:bg-[#F3E5AB] transition-colors duration-300">
                  <Scissors className="w-5 h-5 mb-2" /><span className="font-medium">Book Now</span>
                </Link>
              </div>
              {data.upcoming.length === 0 ? (
                <Empty text="No upcoming appointments." cta />
              ) : (
                <div className="space-y-4">
                  {data.upcoming.map((a) => <ApptCard key={a.id} a={a} onRebook={rebook} onCancel={cancelAppt} onChange={setReschedule} upcoming />)}
                </div>
              )}
            </div>
          )}

          {active === "history" && (
            data.history.length === 0 ? <Empty text="No past visits yet." /> : (
              <div className="space-y-4">{data.history.map((a) => <ApptCard key={a.id} a={a} onRebook={rebook} />)}</div>
            )
          )}

          {active === "notifications" && (
            notes.length === 0 ? <Empty text="No notifications." /> : (
              <div className="space-y-3">
                {notes.map((n) => (
                  <div key={n.id} data-testid={`notif-${n.id}`} className={`p-5 rounded-2xl border flex justify-between gap-4 ${n.read ? "border-white/5 bg-[#141414]" : "border-[#D4AF37]/30 bg-[#D4AF37]/5"}`}>
                    <div><div className="font-medium">{n.title}</div><p className="text-gray-400 text-sm mt-1">{n.message}</p></div>
                    {!n.read && <button onClick={() => markRead(n.id)} className="text-[#D4AF37] shrink-0"><Check className="w-5 h-5" /></button>}
                  </div>
                ))}
              </div>
            )
          )}

          {active === "profile" && (
            <div className="max-w-lg space-y-5">
              <Field label="Name"><input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputCls} /></Field>
              <Field label="Phone"><input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={inputCls} /></Field>
              <Field label="Favourite Barber">
                <select data-testid="profile-fav" value={profile.favourite_barber_id || ""} onChange={(e) => setProfile({ ...profile, favourite_barber_id: e.target.value })} className={inputCls}>
                  <option value="">None</option>
                  {barbers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <button data-testid="profile-save" onClick={saveProfile} className="px-8 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300">Save Changes</button>
            </div>
          )}
        </>
      )}
      {reschedule && <RescheduleModal appt={reschedule} onClose={() => setReschedule(null)} onDone={() => { setReschedule(null); load(); }} />}
    </DashboardShell>
  );
}

const inputCls = "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-0";
const Field = ({ label, children }) => (<div><label className="label-caps block mb-2">{label}</label>{children}</div>);
const Stat = ({ label, value }) => (
  <div className="bg-[#141414] border border-white/5 rounded-2xl p-5"><div className="label-caps mb-1">{label}</div><div className="font-display text-3xl truncate">{value}</div></div>
);
const Empty = ({ text, cta }) => (
  <div className="flex flex-col items-center py-20 text-gray-500">
    <Calendar className="w-12 h-12 mb-4 opacity-50" />{text}
    {cta && <Link to="/book" className="mt-5 px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium">Book Appointment</Link>}
  </div>
);
const ApptCard = ({ a, onRebook, onCancel, onChange, upcoming }) => {
  const cancellable = upcoming && ["pending", "confirmed"].includes(a.status);
  const canCancel = upcoming && a.can_cancel_online === true;
  const withinWindow = cancellable && !canCancel;
  return (
    <div data-testid={`appt-${a.id}`} className="bg-[#141414] border border-white/5 rounded-2xl p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"><Scissors className="w-5 h-5 text-[#D4AF37]" /></span>
          <div>
            <div className="text-lg">{a.service_name}</div>
            <div className="text-gray-400 text-sm">with {a.barber_name}</div>
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{a.date ? a.date.split("-").reverse().join("/") : ""}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{a.start_time}</span>
              {a.ref && <span className="text-[#D4AF37]/80">Ref {a.ref}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-3 py-1 rounded-full ${a.status === "completed" ? "bg-green-500/10 text-green-400" : a.status === "cancelled" ? "bg-red-500/10 text-red-400" : "bg-[#D4AF37]/10 text-[#D4AF37]"}`}>{a.status}</span>
          {canCancel && <button data-testid={`change-${a.id}`} onClick={() => onChange(a)} className="text-[#D4AF37] flex items-center gap-1 text-sm hover:text-[#F3E5AB]"><RefreshCw className="w-4 h-4" /> Change</button>}
          {canCancel && <button data-testid={`cancel-${a.id}`} onClick={() => onCancel(a)} className="text-red-400 flex items-center gap-1 text-sm hover:text-red-300"><X className="w-4 h-4" /> Cancel</button>}
          {!upcoming && <button data-testid={`rebook-${a.id}`} onClick={() => onRebook(a)} className="text-[#D4AF37] flex items-center gap-1 text-sm hover:text-[#F3E5AB]"><RefreshCw className="w-4 h-4" /> Rebook</button>}
        </div>
      </div>
      {withinWindow && (
        <div data-testid={`cancel-blocked-${a.id}`} className="mt-4 pt-4 border-t border-white/5 text-xs text-gray-400 flex items-start gap-2">
          <Clock className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
          Changes and cancellations are unavailable online within 72 hours of your appointment. Please contact Who's Next Barber Shop directly.
        </div>
      )}
    </div>
  );
};

const groupSlots = (slots) => {
  const g = { Morning: [], Afternoon: [], Evening: [] };
  (slots || []).forEach((s) => {
    const h = parseInt(s.split(":")[0], 10);
    if (h < 12) g.Morning.push(s); else if (h < 17) g.Afternoon.push(s); else g.Evening.push(s);
  });
  return Object.fromEntries(Object.entries(g).filter(([, l]) => l.length));
};

const RescheduleModal = ({ appt, onClose, onDone }) => {
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");
  const [saving, setSaving] = useState(false);
  const svcIds = (appt.service_ids && appt.service_ids.length ? appt.service_ids : [appt.service_id]).filter(Boolean).join(",");
  const minDate = new Date(Date.now() + 72 * 3600 * 1000).toISOString().split("T")[0];
  const maxD = new Date(); maxD.setMonth(maxD.getMonth() + 3);
  const maxDate = maxD.toISOString().split("T")[0];

  useEffect(() => {
    if (!date) { setSlots(null); return; }
    setSlots(null); setTime("");
    api.get("/availability", { params: { barber_id: appt.barber_id, service_id: svcIds, date, exclude_id: appt.id } })
      .then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([]));
  }, [date]);

  const submit = async () => {
    if (!date || !time) return;
    setSaving(true);
    try {
      await api.put(`/appointments/${appt.id}/customer-reschedule`, { date, start_time: time });
      toast.success("Appointment changed");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="reschedule-modal" onClick={onClose}>
      <div className="bg-[#141414] border border-white/10 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-white">Change appointment</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="reschedule-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-400 mb-5">{appt.service_name} with {appt.barber_name}. Pick a new date and time (at least 72 hours away).</p>
        <label className="label-caps block mb-2">New date</label>
        <input type="date" data-testid="reschedule-date" min={minDate} max={maxDate} value={date} onChange={(e) => setDate(e.target.value)} className={inputCls + " mb-4"} />
        {date && (
          slots === null ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>
          ) : slots.length === 0 ? (
            <p data-testid="reschedule-no-slots" className="text-sm text-gray-400 py-4">No times available on this date. Please choose another.</p>
          ) : (
            <div className="space-y-4 max-h-56 overflow-y-auto pr-1">
              {Object.entries(groupSlots(slots)).map(([label, list]) => (
                <div key={label}>
                  <div className="label-caps mb-2">{label}</div>
                  <div className="grid grid-cols-4 gap-2">
                    {list.map((s) => (
                      <button key={s} data-testid={`reschedule-slot-${s}`} onClick={() => setTime(s)}
                        className={`py-2 rounded-lg text-sm border transition-colors ${time === s ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/10 text-gray-300 hover:border-[#D4AF37]/50"}`}>{s}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
        <button data-testid="reschedule-submit" disabled={!date || !time || saving} onClick={submit}
          className="mt-6 w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? "Saving…" : "Confirm change"}
        </button>
      </div>
    </div>
  );
};
